import { Badge } from '../../components/ui/Primitives.js';
import { relativeTime } from '../../lib/format.js';
import type { Extension } from '@arcnet/contracts';

/**
 * Three states, not two.
 *
 * "Never seen" is not the same fault as "went offline": one is a handset that
 * was never configured, the other is one that stopped. Collapsing them into
 * "not registered" sends support looking in the wrong place.
 *
 * One definition for the list, its filters and the seat's own header, so the
 * three can never describe the same phone differently.
 */
export type PresenceState = 'online' | 'offline' | 'never';

/**
 * Registered, but not answering the switch's pings (agent phoneping.ts): offline to anyone trying
 * to reach it, a minute after it went away rather than when its registration lapses.
 */
export const isUnreachable = (pingStatus: string | null | undefined): boolean =>
  /unreach|fail/i.test(pingStatus ?? '');

/** What the pings add to "online": the round trip, or that a push service stands in for an app. */
export const pingDetail = (pingStatus: string | null | undefined, ms: number | null | undefined): string =>
  pingStatus === 'push' ? 'via push' : ms != null ? `${ms} ms` : '';

export function presenceOf(ext: Extension): {
  state: PresenceState; tone: 'ok' | 'danger' | 'muted'; label: string; detail: string;
} {
  const reg = ext.registration;
  if (!reg) return { state: 'never', tone: 'muted', label: 'never seen', detail: 'no handset yet' };
  if (reg.registered && isUnreachable(reg.ping_status)) {
    return { state: 'offline', tone: 'danger', label: 'unreachable', detail: 'not answering' };
  }
  if (reg.registered) {
    return { state: 'online', tone: 'ok', label: 'online', detail: pingDetail(reg.ping_status, reg.ping_ms) };
  }
  return {
    state: 'offline', tone: 'danger', label: 'offline',
    detail: reg.last_seen_at ? `seen ${relativeTime(reg.last_seen_at)}` : '',
  };
}

/**
 * ONE LINE: the state, then why, inline and muted.
 *
 * Every cell used to stack a badge over a second line, which made a row three
 * lines tall and gave a transport name the same visual weight as whether the
 * phone works. Transport is gone entirely -- UDP is true of essentially every
 * handset here, so it distinguished nothing and cost a line on every online
 * seat. It is still under Troubleshoot for the one occasion it matters.
 */
export function PresenceCell({ ext }: { ext: Extension }) {
  const p = presenceOf(ext);

  /*
   * The badge holds its width and the detail truncates. A column width alone
   * would not settle this -- narrow the window and the detail starts wrapping
   * again -- so the cell is made incapable of becoming two lines instead.
   */
  return (
    <div className="arc-row" style={{ gap: 7 }}>
      <span style={{ flex: '0 0 auto' }}>
        <Badge tone={p.tone}><span className="arc-dot" aria-hidden="true" />{p.label}</Badge>
      </span>
      {p.detail && (
        <span className="arc-subtle arc-truncate" title={p.detail}
          style={{ fontSize: 11.5, minWidth: 0 }}>{p.detail}</span>
      )}
    </div>
  );
}
